import React, { memo } from "react";
import { nth, remap, clamp, lerpColor, log, antilog } from "@Utils";
import { Rose, RoseConfig } from "@ant-design/charts";
import { useActivityMetrics } from "./activityMetrics";
import { IActivityDetails } from "@Models/IActivityDetails";

interface IProps {
    activity?: IActivityDetails;
    entries?: GraphEntry[];
    height?: number;
    radius?: number;
}

interface GraphEntry {
    type?: string;
    value?: number;
}

const MetricsPercentileGraph: React.FC<IProps> = ({ activity, entries: propEntries, height, radius }: IProps) => {
    // Use the new hook if activity is provided, otherwise use prop entries
    const { entries: hookEntries } = activity ? useActivityMetrics(activity) : { entries: propEntries };
    const entries = activity ? hookEntries : propEntries;

    const rendered = entries?.map((x) => {
        return { type: x.type, value: log(x.value) * 18 };
    });

    const config: RoseConfig = {
        padding: 40,
        animation: false,
        data: rendered,
        xField: "type",
        yField: "value",
        autoFit: true,
        height,
        radius,
        label: {
            offset: -25,
            layout: {
                type: "fixedOverlap",
            },
            formatter: (item) => {
                return item.value < 20 ? "" : item.type;
            },
        },
        yAxis: {
            min: 0,
            max: 80,
            label: null,
            grid: {
                line: {
                    style: {
                        stroke: "#dddddd",
                    },
                },
                alternateColor: "r(0.5, 0.5, 2) 0:#ffffff 1:#ccccff",
                closed: false,
            },
        },
        interactions: [{ type: "element-active" }],
        sectorStyle: function sectorStyle({ value }) {
            return {
                fill:
                    "l(270)" +
                    " 0:" +
                    lerpColor("#457db0", "#5da3e3", "#a1c8eb", clamp(remap(value, 0, 100, -0.25, 1.25), 0, 1)) +
                    " 1:" +
                    lerpColor("#5887b3", "#7eacd7", "#a8c3db", clamp(remap(value, 0, 100, -0.25, 1.25), 0, 1)),
            };
        },
        tooltip: {
            formatter: (datum) => {
                let hint = "";
                if (datum.type == "Speed") {
                    hint =
                        "Improve your speed ranking by reducing your activity completion time. " +
                        "Less time operating means greater efficiency.";
                } else if (datum.type == "Precision") {
                    hint =
                        "This is given by the range and the distribution of accelerations and demonstrates good control. " +
                        "Evidence shows that experienced surgeons tend to have a higher precision score.";
                } else {
                    hint =
                        "We use dimensionless squared jerk (DSJ) to measure the smoothness of your individual hand movements. " +
                        "Research shows a correlation between experience and high movement smoothness.";
                }
                const int: number = Math.round(antilog(datum.value / 18));
                return {
                    name: "Ranking",
                    value: "<br/><b>" + int + nth(int) + " percentile</b><br/><br/>" + hint + "<br/><br/>",
                };
            },
        },
    };
    return <Rose {...config} />;
};

export default memo(MetricsPercentileGraph);

MetricsPercentileGraph.defaultProps = {
    entries: [
        { type: "Speed", value: 0 },
        { type: "Precision", value: 0 },
        { type: "Smoothness", value: 0 }
    ],
};